import React, { useEffect, useRef, useState, useCallback } from "react";
import { MessageSquare, X, Send, Loader2 } from "lucide-react";
import { api, BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Connexis Support — floating live chat widget.
 *
 * Used inside any authenticated portal layout (client / publisher / affiliate).
 * Bottom-right circular launcher → expanded chat panel.
 *
 * WebSocket pushes new replies in real-time. Reconnects with exponential
 * back-off if the socket drops. REST is used for the initial fetch + send so
 * the conversation survives a refresh.
 */
export default function SupportChatWidget() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [thread, setThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [unread, setUnread] = useState(0);
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);
  const reconnectTries = useRef(0);
  const listRef = useRef(null);

  const scrollToBottom = () => {
    requestAnimationFrame(() => {
      if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
    });
  };

  // ── Initial unread poll (cheap) so the badge shows on first render ─────
  useEffect(() => {
    if (!user) return;
    let alive = true;
    const tick = async () => {
      try {
        const r = await api.get("/support/me/unread");
        if (alive) setUnread(r.data?.unread ?? 0);
      } catch { /* noop */ }
    };
    tick();
    const id = setInterval(tick, 30_000);
    return () => { alive = false; clearInterval(id); };
  }, [user]);

  // ── Load thread when panel opens ───────────────────────────────────────
  const loadThread = useCallback(async () => {
    try {
      const r = await api.get("/support/me/thread");
      setThread(r.data?.thread || null);
      setMessages(r.data?.messages || []);
      await api.post("/support/me/read").catch(() => {});
      setUnread(0);
      scrollToBottom();
    } catch (e) {
      console.warn("[support] thread load failed", e);
    }
  }, []);

  useEffect(() => {
    if (open) loadThread();
  }, [open, loadThread]);

  // ── External "open me" event (from /dashboard/support page) ────────────
  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener("connexis:open-support", handler);
    return () => window.removeEventListener("connexis:open-support", handler);
  }, []);

  // ── WebSocket — connect once on mount, reuse across open/close ─────────
  useEffect(() => {
    if (!user) return;
    let mounted = true;

    const connect = () => {
      const token = localStorage.getItem("connexis_token");
      if (!token) return;
      const wsUrl = `${BACKEND_URL.replace(/^http/, "ws")}/api/ws/support?token=${encodeURIComponent(token)}`;
      try {
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;
        ws.onopen = () => { if (mounted) { setConnected(true); reconnectTries.current = 0; } };
        ws.onmessage = (ev) => {
          try {
            const data = JSON.parse(ev.data);
            if (data.type !== "support.new_message") return;
            const m = data.message;
            setMessages((prev) => (prev.some((x) => x.id === m.id) ? prev : [...prev, m]));
            scrollToBottom();
            if (m.from_role === "admin") {
              if (open) {
                api.post("/support/me/read").catch(() => {});
              } else {
                setUnread((u) => u + 1);
              }
            }
          } catch { /* malformed payload */ }
        };
        ws.onclose = () => {
          if (mounted) {
            setConnected(false);
            const delay = Math.min(30_000, 1_500 * Math.pow(2, reconnectTries.current++));
            setTimeout(connect, delay);
          }
        };
        // heartbeat
        const hb = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) ws.send("ping");
          else clearInterval(hb);
        }, 45_000);
      } catch (e) {
        console.warn("[support] ws connect failed", e);
      }
    };
    connect();
    return () => { mounted = false; try { wsRef.current?.close(); } catch { /* ignore */ } };
  // eslint-disable-next-line
  }, [user?.id]);

  const send = async (e) => {
    e?.preventDefault?.();
    const body = draft.trim();
    if (!body || sending) return;
    setSending(true);
    setDraft("");
    try {
      const r = await api.post("/support/me/messages", { body });
      setMessages((prev) => (prev.some((m) => m.id === r.data.id) ? prev : [...prev, r.data]));
      scrollToBottom();
    } catch (err) {
      setDraft(body); // restore on failure
      console.error("[support] send failed", err);
    } finally {
      setSending(false);
    }
  };

  // Don't render for unauthenticated users or admins (admin uses /admin/support page)
  if (!user || user.role === "admin") return null;

  return (
    <div className="fixed bottom-5 right-5 z-40" data-testid="support-chat-widget">
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="relative inline-flex items-center gap-2 rounded-full bg-slate-900 text-white px-4 py-3 shadow-lg hover:bg-slate-800 transition-transform hover:-translate-y-0.5"
          data-testid="support-chat-launcher"
          aria-label="Open Connexis Support chat"
        >
          <MessageSquare className="size-5" strokeWidth={1.8} />
          <span className="text-[13px] font-medium">Support</span>
          {unread > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-semibold inline-flex items-center justify-center" data-testid="support-unread-badge">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      )}

      {open && (
        <div className="w-[360px] max-w-[calc(100vw-2.5rem)] h-[540px] max-h-[calc(100vh-2.5rem)] rounded-2xl bg-white shadow-2xl border border-slate-200 flex flex-col overflow-hidden animate-in slide-in-from-bottom-4 fade-in-0 duration-200" data-testid="support-chat-panel">
          {/* Header */}
          <div className="px-4 py-3 bg-slate-900 text-white flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className={`inline-block size-2 rounded-full ${connected ? "bg-emerald-400" : "bg-amber-400"}`} title={connected ? "Live" : "Reconnecting"} />
              <div>
                <div className="text-[13px] font-semibold leading-tight">Connexis Support</div>
                <div className="text-[10.5px] text-slate-300 leading-tight">Typically replies within 1 hour · 9am–6pm GMT</div>
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="text-slate-300 hover:text-white" data-testid="support-chat-close" aria-label="Close chat"><X className="size-4" /></button>
          </div>

          {/* Messages */}
          <div ref={listRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-2.5 bg-slate-50" data-testid="support-messages">
            {messages.length === 0 && (
              <div className="text-center py-10 text-[12.5px] text-slate-500">
                <MessageSquare className="size-6 text-slate-300 mx-auto mb-2" />
                Send us a message — we usually reply within an hour during UK business hours.
              </div>
            )}
            {messages.map((m) => (
              <div key={m.id} className={`flex ${m.from_role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[82%] rounded-2xl px-3.5 py-2 text-[13px] leading-snug whitespace-pre-wrap break-words ${m.from_role === "user" ? "bg-[#0055FF] text-white rounded-br-md" : "bg-white border border-slate-200 text-slate-800 rounded-bl-md"}`}>
                  {m.from_role === "admin" && <div className="text-[10.5px] uppercase tracking-widest text-[#0055FF] font-semibold mb-0.5">Connexis</div>}
                  <div>{m.body}</div>
                  <div className={`text-[10px] mt-1 ${m.from_role === "user" ? "text-white/60" : "text-slate-400"}`}>{new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Composer */}
          <form onSubmit={send} className="border-t border-slate-100 p-2.5 flex items-end gap-2 bg-white">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Type a message…"
              rows={1}
              className="flex-1 resize-none outline-none text-[13px] px-3 py-2 rounded-lg border border-slate-200 focus:border-[#0055FF] focus:ring-2 focus:ring-[#0055FF]/15 max-h-24"
              data-testid="support-chat-input"
            />
            <button
              type="submit"
              disabled={!draft.trim() || sending}
              className="inline-flex items-center justify-center size-9 rounded-lg bg-[#0055FF] text-white hover:bg-[#0044CC] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              data-testid="support-chat-send"
              aria-label="Send"
            >
              {sending ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-4" />}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
